/**
 * i18next.
 *
 * Словари подключены статическим импортом, а не lazy: суммарно шесть локалей
 * весят порядка 30 КБ gzip, зато нет кадра с сырыми ключами вместо текста —
 * в Telegram WebView такое мерцание выглядит как поломка приложения.
 *
 * compatibilityJSON: 'v3' — ru.json канонический и использует legacy-суффикс
 * `_plural` (units.participants_plural). Формат словаря менять нельзя.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { api, tokenStore } from '@/shared/api/client';
import { LOCALES, type Locale } from '@/shared/api/types';

import de from './locales/de.json';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import ru from './locales/ru.json';
import tr from './locales/tr.json';

export const LOCALE_STORAGE_KEY = 'sg.locale';
export const FALLBACK_LOCALE: Locale = 'ru';

/** Человеческие названия языков — в собственном языке, не переводятся. */
export const LOCALE_LABELS: Record<Locale, string> = {
  ru: 'Русский',
  en: 'English',
  es: 'Español',
  tr: 'Türkçe',
  de: 'Deutsch',
  fr: 'Français',
};

const resources = {
  ru: { translation: ru },
  en: { translation: en },
  es: { translation: es },
  tr: { translation: tr },
  de: { translation: de },
  fr: { translation: fr },
} as const;

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as string[]).includes(value);
}

/** 'ru-RU' -> 'ru'; неизвестное -> null. */
function normalize(tag: string | null | undefined): Locale | null {
  if (!tag) return null;
  const base = tag.toLowerCase().split(/[-_]/)[0];
  return isLocale(base) ? base : null;
}

function fromStorage(): Locale | null {
  try {
    return normalize(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return null;
  }
}

function fromTelegram(): Locale | null {
  return normalize(window.Telegram?.WebApp?.initDataUnsafe.user?.language_code);
}

function fromNavigator(): Locale | null {
  const candidates = [...(navigator.languages ?? []), navigator.language];
  for (const candidate of candidates) {
    const locale = normalize(candidate);
    if (locale) return locale;
  }
  return null;
}

/**
 * Порядок важен: явный выбор пользователя > язык его Telegram-клиента >
 * язык браузера. `users.locale` с сервера применяется позже, в useAuth,
 * и только если пользователь ещё не выбирал язык вручную (D-12).
 */
export function detectLocale(): Locale {
  return fromStorage() ?? fromTelegram() ?? fromNavigator() ?? FALLBACK_LOCALE;
}

const initialLocale = detectLocale();

/**
 * Промис инициализации. main.tsx ЖДЁТ его перед первым рендером: иначе
 * компоненты монтируются до загрузки словарей и t() возвращает сырые ключи
 * (`admin.audit.title` вместо «Журнал») — ровно тот баг, который выглядит
 * как «переводы не работают».
 */
export const i18nReady: Promise<unknown> = i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: FALLBACK_LOCALE,
  supportedLngs: LOCALES,
  defaultNS: 'translation',
  ns: ['translation'],
  compatibilityJSON: 'v3',
  interpolation: {
    // React сам экранирует вывод; двойное экранирование ломает кавычки и «—».
    escapeValue: false,
  },
  returnNull: false,
  returnEmptyString: false,
  react: {
    useSuspense: false,
    // Явно: без этого смена языка не перерисовывает уже смонтированные
    // экраны и требует перезагрузки страницы.
    bindI18n: 'languageChanged loaded',
    bindI18nStore: 'added removed',
  },
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLocale;
}

function persistLocal(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* приватный режим — язык не переживёт перезагрузку, это приемлемо */
  }
}

export interface ChangeLocaleOptions {
  /** Отправить PATCH /client/me/locale. По умолчанию да, если есть сессия. */
  syncRemote?: boolean;
}

/**
 * Меняет язык интерфейса. Локальное состояние применяется немедленно, а не
 * после ответа сервера: смена языка не должна ждать сети.
 */
export async function changeLocale(
  locale: Locale,
  options: ChangeLocaleOptions = {},
): Promise<void> {
  if (!isLocale(locale)) return;
  persistLocal(locale);
  await i18n.changeLanguage(locale);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;

  const authorized = tokenStore.getAccess() !== null || tokenStore.hasRefresh();
  const syncRemote = options.syncRemote ?? authorized;
  if (!syncRemote || !authorized) return;

  try {
    await api.patch<void>('/client/me/locale', { locale });
  } catch {
    // Локаль уже применена локально; расхождение с сервером починится
    // при следующем изменении или логине.
  }
}

/** Применяет серверную локаль (users.locale) при логине, если выбора не было. */
export async function applyRemoteLocale(locale: Locale | null | undefined): Promise<void> {
  if (!isLocale(locale)) return;
  if (fromStorage() !== null) return;
  await i18n.changeLanguage(locale);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

export default i18n;
export { useTranslation } from "react-i18next";
